// FormFields.tsx
import React from 'react';
import type { UseFormRegister, FieldErrors, FieldValues, FieldPath } from 'react-hook-form';
import { UserInput } from './UserInput';
import { UserDropdown, type DropdownOption } from './UserDropdown';

type FieldName<T extends FieldValues> = FieldPath<T>;

type Common<T extends FieldValues> = {
  name: FieldName<T>;
  show?: boolean;
};

type InputField<T extends FieldValues> = Common<T> & {
  kind: 'input';
  label: React.ReactNode;
  unit?: React.ReactNode;
  helpText?: string;
  placeholder?: string;
};

type DropdownField<T extends FieldValues> = Common<T> & {
  kind: 'dropdown';
  label: string;
  options: DropdownOption[];
  helpText?: string;
  placeholder?: string;
};

export type FieldSpec<T extends FieldValues = FieldValues> = InputField<T> | DropdownField<T>;

type Props<T extends FieldValues = FieldValues> = {
  fields: readonly FieldSpec<T>[];
  register: UseFormRegister<T>;
  errors?: FieldErrors<T>;
};

function getFieldError<T extends FieldValues>(errors: FieldErrors<T> | undefined, name: FieldName<T>): string | null {
  const error = errors?.[name as keyof FieldErrors<T>];
  return typeof error?.message === 'string' ? error.message : null;
}

export function FormFields<T extends FieldValues = FieldValues>({ fields, register, errors }: Props<T>) {
  return (
    <>
      {fields.map(f => {
        if (f.show === false) return null;

        if (f.kind === 'input') {
          return <UserInput key={`i:${f.name}`} label={f.label} unit={f.unit} helpText={f.helpText} placeholder={f.placeholder} error={getFieldError(errors, f.name)} {...register(f.name)} />;
        }

        return <UserDropdown key={`d:${f.name}`} label={f.label} options={f.options} helpText={f.helpText} placeholder={f.placeholder} error={getFieldError(errors, f.name)} {...register(f.name)} />;
      })}
    </>
  );
}
